import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CardSkeleton, PageHeaderSkeleton } from '@/components/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { useGetSurveyFormLookupById, useGetSurveySubmissionById } from '../../hooks/use-surveys';
import {
  AnswerValue,
  getFormQuestions,
  Question,
  SurveyFormOption,
  SurveySubmission,
  SurveySubmissionsPagingResult,
} from '../../types/survey.types';

const formatAnswer = (question: Question, answer?: AnswerValue): string => {
  if (!answer) return '-';
  if (question.FormatType === 'multiple-choice') return answer.McOptionsValue ?? '-';
  if (question.FormatType === 'checkboxes') return (answer.CbOptionsValues ?? []).join(', ') || '-';
  if (question.FormatType === 'rating' || question.FormatType === 'nps')
    return String(answer.Rating ?? '-');
  if (question.FormatType === 'likert-scale') {
    return (
      Object.entries(answer.LikertStatementValues ?? {})
        .map(([statement, value]) => `${statement}: ${value}`)
        .join('; ') || '-'
    );
  }
  return answer.Answer ?? '-';
};

export const ResponseViewPage = () => {
  const { t } = useTranslation();
  const { submissionId } = useParams<{ batchId: string; submissionId: string }>();

  const { data: submissionData, isLoading } = useGetSurveySubmissionById(submissionId);
  const submission: SurveySubmission | undefined = (
    submissionData as {
      SurveySubmissions?: SurveySubmissionsPagingResult;
    }
  )?.SurveySubmissions?.items?.[0];

  const { data: formData, isLoading: isFormLoading } = useGetSurveyFormLookupById(
    submission?.SurveyFormId
  );
  const form = (formData as { SurveyForms?: { items: SurveyFormOption[] } })?.SurveyForms
    ?.items?.[0];
  const questions = getFormQuestions(form);

  let answers: Record<string, AnswerValue> = {};
  if (submission?.AnswersJson) {
    try {
      const parsed = JSON.parse(submission.AnswersJson) as AnswerValue[];
      answers = Object.fromEntries(parsed.map((answer) => [answer.QuestionId, answer]));
    } catch {
      answers = {};
    }
  }

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4" aria-busy="true">
        <PageHeaderSkeleton subtitle={false} />
        <CardSkeleton lines={8} />
      </div>
    );
  }
  if (!submission) {
    return <p className="text-sm text-muted-foreground">{t('NO_DATA_FOUND')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <h1 className="text-lg font-semibold">
        {t('RESPONSE_FROM')} {submission.UserName}
      </h1>

      {/* The questions come with the form: placeholders rather than an empty "Survey" card. */}
      {isFormLoading ? (
        <CardSkeleton lines={8} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{form?.Title ?? t('SURVEY')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {questions.map((question) => (
              <div key={question.QuestionId} className="space-y-1 border-b pb-3 last:border-0">
                <p className="text-sm font-medium">{question.Question}</p>
                <p className="text-sm text-muted-foreground">
                  {formatAnswer(question, answers[question.QuestionId])}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
